import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/game-config';
import { NEON_FONTS } from '@/config/colors';

const FONT_TITLE = NEON_FONTS.TITLE;
const FONT_BODY = NEON_FONTS.BODY;

export class PauseScene extends Phaser.Scene {
  private overlay: Phaser.GameObjects.Rectangle | null = null;
  private pausedText: Phaser.GameObjects.Text | null = null;
  private resumeHint: Phaser.GameObjects.Text | null = null;
  private isResuming = false;

  constructor() {
    super({ key: 'PauseScene' });
  }

  create(): void {
    this.cleanupObjects();
    this.isResuming = false;

    // Semi-transparent dark overlay to dim the game underneath
    this.overlay = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x000000,
      0.7,
    );
    this.overlay.setOrigin(0.5);

    // "PAUSED" text - large and prominent with neon glow
    this.pausedText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 20,
      'PAUSED',
      {
        fontFamily: FONT_TITLE,
        fontSize: '28px',
        fontStyle: 'bold',
        color: '#FFE500',
        shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 16, fill: true },
      },
    ).setOrigin(0.5);

    // Add pulsing effect to PAUSED text
    this.tweens.add({
      targets: this.pausedText,
      alpha: { from: 1, to: 0.5 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Resume hint text
    this.resumeHint = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 30,
      'PRESS ESC OR P TO RESUME',
      {
        fontFamily: FONT_BODY,
        fontSize: '12px',
        color: '#667788',
      },
    ).setOrigin(0.5);

    // Listen for unpause keys - these will be handled by GameScene
    // PauseScene only listens to resume itself when keys are pressed
    this.setupResumeKeys();
  }

  private setupResumeKeys(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) {
      return;
    }

    const resumeHandler = (event: KeyboardEvent): void => {
      const key = event.key.toUpperCase();
      const code = event.code;

      if (
        key === 'ESCAPE' ||
        key === 'P' ||
        code === 'F3' ||
        code === 'Pause'
      ) {
        this.resumeGame();
      }
    };

    keyboard.on('keydown', resumeHandler);
  }

  private resumeGame(): void {
    // Guard against double-resume from rapid key presses
    if (this.isResuming) {
      return;
    }
    this.isResuming = true;

    // Kill all tweens to prevent lingering animations
    this.tweens.killAll();

    // Stop the PauseScene overlay
    this.scene.stop('PauseScene');

    // Resume the GameScene underneath
    this.scene.resume('GameScene');
  }

  private cleanupObjects(): void {
    if (this.overlay) {
      this.overlay.destroy();
      this.overlay = null;
    }
    if (this.pausedText) {
      this.pausedText.destroy();
      this.pausedText = null;
    }
    if (this.resumeHint) {
      this.resumeHint.destroy();
      this.resumeHint = null;
    }
  }
}
